import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API } from "@/lib/api";

const formatCurrency = (amount) =>
  `\u20B9${Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

/**
 * Reusable dialog to record a supplier payment (money out) or customer receipt (money in).
 * Props:
 *  - open, onOpenChange
 *  - kind: 'supplier' (default) | 'customer'
 *  - supplierName (party display name)
 *  - basePayload: { credit_account_id?, supplier_id?, supplier_name?, target }
 *  - owedAmount: number (pre-known total owed; if not provided it is fetched)
 *  - onPaid: callback after a successful payment
 */
export const SupplierPaymentDialog = ({ open, onOpenChange, kind = "supplier", supplierName, basePayload, owedAmount, onPaid }) => {
  const isCustomer = kind === "customer";
  const endpoint = isCustomer ? "customer-payments" : "supplier-payments";
  const [owed, setOwed] = useState(owedAmount ?? null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [bankAccountId, setBankAccountId] = useState("");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset on open
    setMethod("cash");
    setBankAccountId("");
    setReference("");
    setNotes("");
    setPaymentDate(new Date().toISOString().slice(0, 10));

    const init = async () => {
      try {
        const bankRes = await axios.get(`${API}/bank-accounts`);
        setBankAccounts(Array.isArray(bankRes.data) ? bankRes.data : []);
      } catch (e) {
        console.error("Failed to load bank accounts", e);
      }

      let total = owedAmount;
      if (total == null) {
        try {
          const params = new URLSearchParams();
          if (basePayload.credit_account_id) params.append("credit_account_id", basePayload.credit_account_id);
          if (basePayload.supplier_id) params.append("supplier_id", basePayload.supplier_id);
          const res = await axios.get(`${API}/${endpoint}/owed?${params.toString()}`);
          total = basePayload.target === "opening_balance" ? res.data.opening_balance : res.data.total_owed;
        } catch (e) {
          console.error("Failed to fetch owed amount", e);
          total = 0;
        }
      }
      setOwed(total);
      setAmount(total ? String(total) : "");
    };
    init();
  }, [open, owedAmount, basePayload.credit_account_id, basePayload.supplier_id, basePayload.target, endpoint]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid payment amount");
      return;
    }
    if (owed != null && amt > Number(owed) + 0.01) {
      toast.error(`Amount cannot exceed amount owed (${formatCurrency(owed)})`);
      return;
    }
    if (method === "bank_transfer" && !bankAccountId) {
      toast.error("Select a bank account");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/${endpoint}`, {
        ...basePayload,
        amount: amt,
        payment_method: method,
        bank_account_id: method === "bank_transfer" ? bankAccountId : null,
        reference: reference || null,
        notes: notes || null,
        payment_date: paymentDate,
      });
      toast.success(`${isCustomer ? "Payment of" : "Payment of"} ${formatCurrency(amt)} recorded for ${supplierName}`);
      onOpenChange(false);
      if (onPaid) onPaid();
    } catch (err) {
      console.error("Payment failed", err);
      toast.error(err.response?.data?.detail || "Failed to record payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="supplier-payment-dialog">
        <DialogHeader>
          <DialogTitle>{isCustomer ? `Receive Payment — ${supplierName}` : `Pay Supplier — ${supplierName}`}</DialogTitle>
          <DialogDescription>
            {basePayload.target === "opening_balance"
              ? `Record a payment against this ${isCustomer ? "customer" : "supplier"}'s opening balance.`
              : isCustomer
                ? "Record a payment received. It settles the opening balance first, then outstanding receivables. Money goes into your cash/bank."
                : "Record a payment. It settles the opening balance first, then outstanding payables."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="rounded-md bg-blue-50 border border-blue-100 p-3 text-sm text-blue-900" data-testid="supplier-owed-amount">
            Amount owed: <strong>{owed == null ? "…" : formatCurrency(owed)}</strong>
          </div>

          <div>
            <Label htmlFor="sp-amount">Payment Amount (₹) *</Label>
            <Input
              id="sp-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="no-uppercase"
              data-testid="supplier-payment-amount"
              required
            />
          </div>

          <div>
            <Label htmlFor="sp-method">Payment Method *</Label>
            <select
              id="sp-method"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 no-uppercase"
              data-testid="supplier-payment-method"
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>

          {method === "bank_transfer" && (
            <div>
              <Label htmlFor="sp-bank">Bank Account *</Label>
              <select
                id="sp-bank"
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 no-uppercase"
                data-testid="supplier-payment-bank"
              >
                <option value="">-- Select bank account --</option>
                {bankAccounts.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.bank_name}{b.account_number ? ` - ${b.account_number}` : ""} {b.current_balance != null ? `(${formatCurrency(b.current_balance)})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <Label htmlFor="sp-date">Payment Date</Label>
            <Input
              id="sp-date"
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              data-testid="supplier-payment-date"
            />
          </div>

          <div>
            <Label htmlFor="sp-ref">Reference (optional)</Label>
            <Input
              id="sp-ref"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="Cheque no., UTR, etc."
              className="no-uppercase"
              data-testid="supplier-payment-reference"
            />
          </div>

          <div>
            <Label htmlFor="sp-notes">Notes (optional)</Label>
            <textarea
              id="sp-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none no-uppercase"
              data-testid="supplier-payment-notes"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="bg-green-600 hover:bg-green-700" data-testid="supplier-payment-submit">
              {submitting ? "Recording..." : "Record Payment"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierPaymentDialog;
